<template>
  <div class="imoveis-page">
    <!-- Page header -->
    <div class="page-header">
      <div class="page-header-inner">
        <div>
          <div class="page-title">Todos os Imóveis</div>
          <div class="page-subtitle">
            Visão geral de todos os anúncios publicados
          </div>
        </div>
        <q-btn
          class="gt-sm publish-btn"
          label="Publicar Imóvel"
          icon="add_circle"
          unelevated
          to="/imovel-steped/adicionar"
        />
      </div>
    </div>

    <!-- Content -->
    <div class="content-wrapper">
      <!-- Loading skeletons -->
      <div v-if="loading" class="row q-col-gutter-lg">
        <div
          v-for="n in 8"
          :key="n"
          class="col-12 col-sm-6 col-md-4 col-lg-3"
        >
          <div class="skeleton-card">
            <q-skeleton height="180px" class="skeleton-img" />
            <div class="q-pa-md">
              <q-skeleton type="text" width="60%" class="q-mb-sm" />
              <q-skeleton type="text" width="90%" class="q-mb-xs" />
              <q-skeleton type="text" width="75%" />
            </div>
          </div>
        </div>
      </div>

      <!-- Error state -->
      <div v-else-if="propertiesError" class="empty-state error-state">
        <div class="empty-icon-wrap">
          <q-icon name="cloud_off" size="48px" color="negative" />
        </div>
        <div class="empty-title">Não foi possível carregar os imóveis</div>
        <div class="empty-sub">{{ propertiesError }}</div>
        <q-btn
          class="q-mt-md"
          color="primary"
          icon="refresh"
          label="Tentar novamente"
          unelevated
          @click="getProperties(currentPage)"
        />
      </div>

      <!-- Empty state -->
      <div v-else-if="imoveis.length === 0" class="empty-state">
        <div class="empty-icon-wrap">
          <q-icon name="home_work" size="48px" color="grey-4" />
        </div>
        <div class="empty-title">Nenhum imóvel encontrado</div>
        <div class="empty-sub">Ainda não existem imóveis publicados.</div>
      </div>

      <!-- Grid -->
      <div v-else class="row q-col-gutter-lg">
        <div
          v-for="(imovel, index) in imoveis"
          :key="imovel.id || imovel.slug || index"
          class="col-12 col-sm-6 col-md-4 col-lg-3"
        >
          <q-card
            class="imovel-card"
            :class="{ 'card-featured': imovel.is_featured }"
            flat
          >
            <!-- Image -->
            <div class="image-container">
              <q-badge
                class="tipo-negocio-badge"
                :class="
                  imovel.objective === 'Venda'
                    ? 'badge-venda'
                    : 'badge-arrendamento'
                "
              >
                {{ imovel.objective }}
              </q-badge>

              <div
                v-if="highlightLabel(imovel)"
                class="featured-badge"
                :class="highlightClass(imovel)"
                :title="highlightLabel(imovel)"
              >
                <q-icon :name="highlightIcon(imovel)" size="12px" />
                {{ highlightLabel(imovel) }}
              </div>

              <q-badge
                v-if="imovel.status"
                class="review-status-badge"
                :class="statusClass(imovel.status)"
              >
                {{ statusLabel(imovel.status) }}
              </q-badge>

              <q-img
                :src="imovel.primary_image_url"
                :ratio="16 / 10"
                class="cursor-pointer card-img"
                @click="verDetalhes(imovel)"
              >
                <template v-slot:loading>
                  <q-spinner-gears color="white" />
                </template>
              </q-img>
            </div>

            <!-- Body -->
            <q-card-section class="card-body">
              <div class="property-type">{{ imovel.property_type }}</div>
              <div class="property-title ellipsis-2-lines">
                {{ imovel.title }}
              </div>

              <div v-if="imovel.rejection_reason" class="rejection-note">
                <q-icon name="info" size="14px" />
                {{ imovel.rejection_reason }}
              </div>

              <div class="location-row">
                <q-icon name="location_on" size="14px" />
                <span>{{ imovel.location }}</span>
              </div>

              <!-- Stats row -->
              <div class="stats-row">
                <div class="stat">
                  <q-icon name="bed" size="16px" />
                  <span>{{ imovel.bedroom_amount }}</span>
                </div>
                <div class="stat">
                  <q-icon name="bathtub" size="16px" />
                  <span>{{ imovel.bathroom_amount }}</span>
                </div>
                <div class="stat">
                  <q-icon name="square_foot" size="16px" />
                  <span>{{ imovel.area }}m²</span>
                </div>
                <div class="stat">
                  <q-icon name="garage" size="16px" />
                  <span>{{ imovel.garage_space }}</span>
                </div>
                <div class="stat">
                  <q-icon name="visibility" size="16px" />
                  <span>{{ imovel.clicks }}</span>
                </div>
              </div>

              <div class="card-divider" />

              <!-- Price + actions -->
              <div class="price-row">
                <div class="price">
                  {{ formatPrice(imovel.price, imovel.currency) }}
                </div>
                <div class="action-btns">
                  <button
                    class="star-btn"
                    :class="{ 'star-active': imovel.is_featured }"
                    :title="
                      imovel.is_featured ? 'Remover destaque' : 'Destacar imóvel'
                    "
                    @click.stop="toggleFeatured(imovel)"
                  >
                    <q-icon
                      :name="imovel.is_featured ? 'star' : 'star_border'"
                      size="18px"
                    />
                  </button>
                  <button
                    v-if="canEdit(imovel)"
                    class="edit-btn"
                    title="Editar imóvel"
                    @click.stop="editarImovel(imovel)"
                  >
                    <q-icon name="edit" size="16px" />
                  </button>
                  <button
                    class="detail-btn"
                    title="Ver detalhes"
                    @click="verDetalhes(imovel)"
                  >
                    <q-icon name="arrow_forward" size="18px" />
                  </button>
                </div>
              </div>
            </q-card-section>
          </q-card>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="!loading && totalPages > 1" class="pagination-row">
        <q-pagination
          v-model="currentPage"
          :max="totalPages"
          :max-pages="6"
          direction-links
          boundary-links
          color="primary"
          active-design="unelevated"
          active-color="primary"
          active-text-color="white"
          @update:model-value="getProperties(currentPage)"
        />
      </div>
    </div>

    <!-- FAB mobile -->
    <q-page-sticky position="bottom-right" :offset="[24, 24]" class="lt-md">
      <q-btn
        fab
        color="secondary"
        icon="add"
        size="xl"
        to="/imovel-steped/adicionar"
      />
    </q-page-sticky>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import Swal from "sweetalert2";
import store from "@/vuex/store";
import router from "@/router";

const currentPage = ref(1);
const totalPages = ref(1);
const loading = ref(true);

const imoveis = ref([]);
const propertiesError = ref("");

onMounted(() => {
  getProperties();
});

function getProperties(page = 1) {
  loading.value = true;
  propertiesError.value = "";
  imoveis.value = [];
  store
    .dispatch("imovel/getAllImovel", page)
    .then(() => {
      imoveis.value = store.state.imovel.data.data;
      totalPages.value = store.state.imovel.data.last_page;
    })
    .catch((error) => {
      propertiesError.value =
        error.response?.data?.message ||
        "Verifique a ligação e tente novamente.";
    })
    .finally(() => {
      loading.value = false;
    });
}

const formatPrice = (price, currency) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
  }).format(price);
};

const statusLabel = (status) =>
  ({
    active: "Publicado",
    published: "Publicado",
    pending_review: "Aguarda validação",
    draft: "Rascunho",
    rejected: "Requer correcção",
    inactive: "Inactivo",
    expired: "Expirado",
  }[status] ||
  status ||
  "Rascunho");

const statusClass = (status) => {
  if (status === "active" || status === "published") return "status-active";
  if (status === "pending_review") return "status-pending";
  if (["expired", "inactive", "rejected"].includes(status))
    return "status-problem";
  return "status-draft";
};

const highlightLabel = (imovel) =>
  imovel.promotion?.badge_label ||
  (imovel.is_featured || imovel.promotion?.editorial_featured
    ? "Destaque editorial"
    : imovel.verified || imovel.verified_at
      ? "Verificado"
      : null);

const highlightClass = (imovel) =>
  imovel.promotion?.badge_label || imovel.is_featured || imovel.promotion?.editorial_featured
    ? "badge-featured"
    : "badge-standard";

const highlightIcon = (imovel) =>
  imovel.is_featured || imovel.promotion?.editorial_featured
    ? "star"
    : imovel.promotion?.badge_label
      ? "workspace_premium"
      : "verified";

const verDetalhes = (imovel) => {
  router.push({ name: "imovel-details", params: { slug: imovel.slug } });
};

const canEdit = (imovel) => {
  const user = store.state.auth.user;
  if (!user) return false;

  return (
    user.role === 1 || user.id === imovel.user_id || user.id === imovel.user?.id
  );
};

const editarImovel = (imovel) => {
  if (!imovel.slug) return;
  router.push({ name: "edit-imovel-steped", params: { slug: imovel.slug } });
};

const toggleFeatured = (imovel) => {
  store
    .dispatch("imovel/toggleFeatured", imovel.slug)
    .then((res) => {
      imovel.is_featured = res.data.is_featured;
      Swal.fire({
        icon: res.data.is_featured ? "success" : "info",
        title: res.message,
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 2500,
        timerProgressBar: true,
      });
    })
    .catch(() => {
      Swal.fire({
        icon: "error",
        title: "Erro ao actualizar destaque.",
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 2500,
      });
    });
};
</script>

<style scoped>
/* ── Page ─────────────────────────────────────────────── */
.imoveis-page {
  background: #f4f6fb;
  min-height: 100vh;
}

/* ── Header ───────────────────────────────────────────── */
.page-header {
  background: #0d1b2a;
  padding: 20px 24px;
}
.page-header-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1400px;
  margin: 0 auto;
  gap: 12px;
}
.page-title {
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
}
.page-subtitle {
  font-size: 12px;
  color: rgba(212, 175, 55, 0.75);
  letter-spacing: 0.4px;
  margin-top: 2px;
}
.publish-btn {
  background: #d4af37 !important;
  color: #0d1b2a !important;
  font-weight: 700;
  border-radius: 10px;
  font-size: 13px;
  letter-spacing: 0.3px;
}

/* ── Content ──────────────────────────────────────────── */
.content-wrapper {
  max-width: 1400px;
  margin: 28px auto;
  padding: 0 20px 48px;
}

/* ── Skeleton ─────────────────────────────────────────── */
.skeleton-card {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #e8ecf4;
}
.skeleton-img {
  border-radius: 0;
}

/* ── Empty / error state ──────────────────────────────── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 80px 16px;
  text-align: center;
}
.empty-icon-wrap {
  width: 88px;
  height: 88px;
  background: #fff;
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e8ecf4;
  box-shadow: 0 4px 16px rgba(13, 27, 42, 0.06);
  margin-bottom: 20px;
}
.empty-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}
.empty-sub {
  font-size: 13px;
  color: #94a3b8;
  margin-top: 6px;
}

/* ── Card ─────────────────────────────────────────────── */
.imovel-card {
  height: 100%;
  border-radius: 16px;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid #e8ecf4;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}
.imovel-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 16px 40px rgba(13, 27, 42, 0.12);
}
.card-featured {
  border-color: #d4af37 !important;
  box-shadow: 0 0 0 1px #d4af37;
}
.card-featured:hover {
  box-shadow: 0 0 0 1px #d4af37, 0 16px 40px rgba(13, 27, 42, 0.12);
}

/* ── Image ────────────────────────────────────────────── */
.image-container {
  position: relative;
  overflow: hidden;
}
.card-img {
  transition: transform 0.35s ease;
  display: block;
}
.imovel-card:hover .card-img {
  transform: scale(1.05);
}

/* ── Badges ───────────────────────────────────────────── */
.tipo-negocio-badge {
  position: absolute;
  bottom: 12px;
  left: 12px;
  z-index: 1;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 700;
  border-radius: 20px;
  letter-spacing: 0.4px;
}
.badge-venda {
  background: rgba(22, 163, 74, 0.92) !important;
  color: #fff !important;
}
.badge-arrendamento {
  background: rgba(37, 99, 235, 0.92) !important;
  color: #fff !important;
}

/* Featured badge (top-left) */
.featured-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 2;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.4px;
  padding: 3px 9px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  gap: 3px;
}
.badge-featured {
  background: rgba(212, 175, 55, 0.95);
  color: #0d1b2a;
}
.badge-standard {
  background: rgba(13, 27, 42, 0.72);
  color: #fff;
}

/* Review status badge (top-right) */
.review-status-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  padding: 4px 9px;
  border-radius: 20px;
  color: #fff !important;
  font-size: 10px;
  font-weight: 700;
}
.status-active {
  background: rgba(22, 163, 74, 0.92) !important;
}
.status-pending {
  background: rgba(37, 99, 235, 0.92) !important;
}
.status-problem {
  background: rgba(185, 28, 28, 0.9) !important;
}
.status-draft {
  color: #0d1b2a !important;
  background: rgba(212, 175, 55, 0.94) !important;
}

/* ── Card body ────────────────────────────────────────── */
.card-body {
  padding: 16px !important;
  position: relative;
  z-index: 2;
}
.property-type {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #d4af37;
  margin-bottom: 4px;
}
.property-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.35;
  margin-bottom: 8px;
}
.ellipsis-2-lines {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 38px;
}
.rejection-note {
  display: flex;
  gap: 5px;
  margin: 0 0 9px;
  padding: 7px 8px;
  border-radius: 7px;
  color: #9f341e;
  background: #fff3ef;
  font-size: 10px;
  line-height: 1.35;
}
.location-row {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 12px;
}
.location-row .q-icon {
  color: #94a3b8;
  flex-shrink: 0;
}

/* ── Stats ────────────────────────────────────────────── */
.stats-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 12px;
}
.stat {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}
.stat .q-icon {
  color: #94a3b8;
}

/* ── Divider ──────────────────────────────────────────── */
.card-divider {
  height: 1px;
  background: #f1f5f9;
  margin: 12px 0;
}

/* ── Price row ────────────────────────────────────────── */
.price-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.price {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 17px;
  font-weight: 800;
  color: #0d1b2a;
  letter-spacing: -0.3px;
  white-space: nowrap;
}
.action-btns {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  margin-left: auto;
}
.star-btn,
.edit-btn,
.detail-btn {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.18s, color 0.18s;
}
.star-btn {
  background: #f1f5f9;
  color: #94a3b8;
}
.star-btn:hover,
.star-active {
  background: #fef9ec !important;
  color: #d4af37 !important;
}
.edit-btn {
  background: #f1f5f9;
  color: #64748b;
}
.edit-btn:hover {
  background: #fef9ec;
  color: #b08b1e;
}
.detail-btn {
  background: #0d1b2a;
  color: #d4af37;
}
.detail-btn:hover {
  background: #162333;
}

/* ── Pagination ───────────────────────────────────────── */
.pagination-row {
  display: flex;
  justify-content: center;
  padding-top: 36px;
}
</style>